import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { findAllRelease } from "../../../../api/inventoryService";
import baseUrl from "../../../../api/baseUrl";
import { printElementToPdf } from "../../../../utils/printElement";
import { toast } from "react-toastify";
import InventoryIcon from "@mui/icons-material/Inventory";
import PrintIcon from "@mui/icons-material/Print";
import ReleaseReceipt from "./ReleaseReceipt";
import ReleaseTypeSwitch from "../components/ReleaseTypeSwitch";
import { isReleaseType, ReleaseType, staffName } from "../components/releaseTypes";
import { costLabel, hasUncosted, marginLabel, marginTone } from "../components/releaseCost";

// Cost and Margin come from the FIFO ledger (releaseCost.ts); they are internal and never printed on receipts.
const PAID_HEADER = ["No", "Name", "Receipt", "Total", "Discount", "Net Total", "Cost", "Margin", "Card", "Cash", "Date", "Action"];
const CLINIC_HEADER = ["No", "Taken by", "Products", "Cost", "Date", "Action"];
const TREATMENT_HEADER = ["No", "Patient", "Treatment · Stage", "Done by", "Products", "Cost", "Date", "Action"];

/** "Crown - Zirconia · Fit" */
const treatmentStage = (item: any) =>
  [item?.treatmentName, item?.stage].filter(Boolean).join(" · ") || "—";

const personName = (item: any) =>
  item?.type === "patient" || item?.patientId
    ? item?.patientId?.fullName
    : staffName(item?.employeeId);

/** "Gloves × 2, Masks × 1" */
const itemsSummary = (item: any) =>
  (item?.product || [])
    .map((line: any) => `${line?.productId?.productName || "Product"} × ${line?.quantity}`)
    .join(", ");

/** Cost cell: the FIFO figure, or a dash with a hint when part of it is uncosted. */
const CostCell = ({ item, className = "" }: { item: any; className?: string }) => (
  <span className={className} title={hasUncosted(item) ? `${item.uncostedQty} unit(s) left stock with no purchase lot behind them` : undefined}>
    {costLabel(item)}
    {hasUncosted(item) && <span className="ml-1 text-[10px] text-amber-600 dark:text-amber-400">partly uncosted</span>}
  </span>
);

function AllRelease() {
  const [params, setParams] = useSearchParams();
  const raw = params.get("type");
  const select: ReleaseType = isReleaseType(raw) ? raw : "patient";
  const [data, setdata] = useState([]);
  const [clinic, setClinic] = useState<any>(null);
  const [printRelease, setPrintRelease] = useState<any>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const isClinic = select === "clinic";
  const isTreatment = select === "treatment";

  const getReleaseData = useCallback(async (select: string) => {
    try {
      const data = await findAllRelease(select);
      setdata(data as never[]);
    } catch (error) {
      console.error(error);
    }
  }, []);

  useEffect(() => {
    getReleaseData(select);
  }, [getReleaseData, select]);

  useEffect(() => {
    baseUrl
      .get("/clinic/getBillData")
      .then((response) => setClinic(response.data?.data || null))
      .catch((error) => console.error(error));
  }, []);

  const handlePrintReceipt = useCallback((item: any) => {
    setPrintRelease(item);
  }, []);

  useEffect(() => {
    if (!printRelease || !printRef.current) return;
    printElementToPdf(printRef.current)
      .catch((error) => {
        console.error(error);
        toast.error("Could not print the receipt.");
      })
      .finally(() => setPrintRelease(null));
  }, [printRelease]);

  const handleChange = (value: ReleaseType) => {
    setdata([]);
    setParams({ type: value }, { replace: true });
  };

  const tableHeader = isTreatment ? TREATMENT_HEADER : isClinic ? CLINIC_HEADER : PAID_HEADER;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 p-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-primary/10 dark:bg-primary/30 rounded-md flex items-center justify-center">
              <InventoryIcon className="text-primary dark:text-sky-300" sx={{ fontSize: 14 }} />
            </div>
            <div>
              <h2 className="text-xs font-bold text-slate-800 dark:text-slate-100">All Releases</h2>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">{data.length} records found</p>
            </div>
          </div>
          <ReleaseTypeSwitch value={select} onChange={handleChange} />
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="md:hidden space-y-2">
        {data?.map((item: any, i: number) => (
          <div key={i} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-100 dark:border-slate-700 p-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">
                #{i + 1}
                {item?.invoiceNo ? ` · ${item.invoiceNo}` : ""}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 dark:text-slate-400">{item?.createdAt?.split("T")[0]}</span>
                <button
                  type="button"
                  onClick={() => handlePrintReceipt(item)}
                  className="w-6 h-6 rounded-md bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center"
                  aria-label="Print receipt"
                >
                  <PrintIcon sx={{ fontSize: 12 }} />
                </button>
              </div>
            </div>
            <h3 className="font-semibold text-xs text-slate-800 dark:text-slate-100 mb-2">
              {personName(item)}
            </h3>
            {isTreatment ? (
              <div className="space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
                <p>{treatmentStage(item)}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Done by {staffName(item?.employeeId) || "—"}</p>
                <p>{itemsSummary(item)}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Cost <CostCell item={item} className="font-semibold text-slate-700 dark:text-slate-200" /></p>
              </div>
            ) : isClinic ? (
              <div className="space-y-0.5 text-xs text-slate-600 dark:text-slate-300">
                <p>{itemsSummary(item)}</p>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">Cost <CostCell item={item} className="font-semibold text-slate-700 dark:text-slate-200" /></p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <div className="bg-slate-50 dark:bg-slate-700 rounded-md p-1.5">
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">Total</p>
                  <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">{item?.total}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700 rounded-md p-1.5">
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">Discount</p>
                  <p className="font-semibold text-xs text-slate-700 dark:text-slate-200">{item?.discount}</p>
                </div>
                <div className="bg-emerald-50 dark:bg-emerald-900/40 rounded-md p-1.5">
                  <p className="text-emerald-600 dark:text-emerald-300 text-[10px]">Net Total</p>
                  <p className="font-semibold text-xs text-emerald-700 dark:text-emerald-300">{item?.netTotal}</p>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-900/40 rounded-md p-1.5">
                  <p className="text-indigo-600 dark:text-indigo-300 text-[10px]">Card / Cash</p>
                  <p className="font-semibold text-xs text-indigo-700 dark:text-indigo-300">{item?.card} / {item?.cash}</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-700 rounded-md p-1.5">
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">Cost</p>
                  <CostCell item={item} className="font-semibold text-xs text-slate-700 dark:text-slate-200" />
                </div>
                <div className="bg-slate-50 dark:bg-slate-700 rounded-md p-1.5">
                  <p className="text-slate-500 dark:text-slate-400 text-[10px]">Margin</p>
                  <p className={`font-semibold text-xs ${marginTone(item)}`}>{marginLabel(item)}</p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
        <table className="w-full">
          <thead className="border-b border-slate-200 dark:border-slate-600">
            <tr>
              {tableHeader.map((title, i) => (
                <th key={i} className="px-3 py-2 text-left text-xs font-semibold text-slate-500 dark:text-slate-300 uppercase tracking-wider">
                  {title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-600">
            {data?.map((item: any, i: number) => (
              <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{i + 1}</td>
                <td className="px-3 py-2 text-xs font-medium text-slate-800 dark:text-slate-100">
                  {personName(item)}
                </td>
                {isTreatment ? (
                  <>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{treatmentStage(item)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{staffName(item?.employeeId) || "—"}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{itemsSummary(item)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300"><CostCell item={item} /></td>
                  </>
                ) : isClinic ? (
                  <>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{itemsSummary(item)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300"><CostCell item={item} /></td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-xs text-indigo-600 dark:text-indigo-300 font-medium">
                      {item?.invoiceNo ? `#${item.invoiceNo}` : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.total}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.discount}</td>
                    <td className="px-3 py-2 text-xs font-semibold text-emerald-600 dark:text-emerald-300">{item?.netTotal}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300"><CostCell item={item} /></td>
                    <td className={`px-3 py-2 text-xs font-semibold ${marginTone(item)}`}>{marginLabel(item)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.card}</td>
                    <td className="px-3 py-2 text-xs text-slate-600 dark:text-slate-300">{item?.cash}</td>
                  </>
                )}
                <td className="px-3 py-2 text-xs text-slate-500 dark:text-slate-400">{item?.createdAt?.split("T")[0]}</td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => handlePrintReceipt(item)}
                    className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-800/50 transition-colors"
                    title="Print receipt"
                    aria-label="Print receipt"
                  >
                    <PrintIcon sx={{ fontSize: 14 }} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ position: "absolute", left: "-9999px" }}>
        <div ref={printRef}>
          {printRelease && (
            <ReleaseReceipt clinic={clinic} release={printRelease} />
          )}
        </div>
      </div>
    </div>
  );
}

export default AllRelease;
